// Public post-hire "Joining Details" portal — accessed via
// /onboarding-form/:token, no login. Separate link/token from the
// recruitment candidate-portal, sent once a candidate is marked Joined.
// Collects personal/address/family details not gathered during recruitment
// or BGV, plus a handful of document uploads (photo, education marksheets,
// salary slips, bank statement).
//
// The candidate fills in every part (details, PF declarations, documents)
// and presses one Submit at the bottom. Nothing is sent to HR until then.

import React, { useEffect, useState } from "react";
import { K } from "../theme";
import { CheckCircle2, Upload, AlertCircle, FileText, X } from "lucide-react";
import { BASE } from "../api";
import { fmtDDMMYY } from "../dateFormat";
import { FormRenderer } from "../components/FormRenderer";

// Grouped to mirror the sections HR already tracks in Zoho People's
// Candidate Details record — Personal / Identity Numbers / Address /
// Previous Employment / Family. This form intentionally overlaps some of
// the BGV form's questions (e.g. previous company, last working date) —
// the BGV form itself is unchanged.
const FIELD_SECTIONS = [
  {
    title: "Personal Details",
    fields: [
      ["date_of_birth", "Date of Birth", "date"],
      ["gender", "Gender", "text"],
      ["marital_status", "Marital Status", "text"],
      ["blood_group", "Blood Group", "text"],
      ["nationality", "Nationality", "text"],
    ],
  },
  {
    title: "Identity Numbers",
    fields: [
      ["aadhaar_number", "Aadhaar Card Number", "text"],
      ["pan_number", "PAN Card Number", "text"],
    ],
  },
  {
    title: "Address Details",
    fields: [
      ["present_address", "Present Address", "text"],
      ["permanent_address", "Permanent Address", "text"],
    ],
  },
  {
    title: "Previous Employment Details",
    fields: [
      ["previous_company", "Company", "text"],
      ["department", "Department", "text"],
      ["business_unit", "Business Unit", "text"],
      ["division", "Division", "text"],
      ["current_salary", "Current Salary", "text"],
      ["last_working_date_prev", "Last Working Date", "date"],
    ],
  },
  {
    title: "Family Details",
    fields: [
      ["mother_name", "Mother's Name", "text"],
      ["mother_contact", "Mother's Contact Number", "text"],
      ["father_name", "Father's Name", "text"],
      ["father_contact", "Father's Contact Number", "text"],
    ],
  },
];

export const OnboardingJoiningPortal = ({ token }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [uploading, setUploading] = useState(null);
  const [msg, setMsg] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  // PF declarations — one answer bag per form, keyed by field id.
  const [pf, setPf] = useState({});

  // `initial` restores the saved answers. Later reloads (after an upload or
  // a removal) only refresh the documents, so anything typed but not yet
  // submitted stays on screen.
  const load = (initial = false) => {
    setLoading(true);
    fetch(`${BASE}/api/onboarding-portal/${token}`)
      .then(r => r.json().then(j => ({ ok: r.ok, j })))
      .then(({ ok, j }) => {
        if (ok) {
          setData(j);
          if (initial) { setForm(j.joiningDetails || {}); setPf(j.pfAnswers || {}); }
          setErr(null);
        } else setErr(j.error || "Failed to load");
        setLoading(false);
      })
      .catch(e => { setErr(e.message); setLoading(false); });
  };
  useEffect(() => load(true), [token]);

  const flash = (text, kind = "ok") => { setMsg({ text, kind }); setTimeout(() => setMsg(null), 3000); };

  const upload = async (docType, file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) return flash("File too large (max 10MB)", "err");
    setUploading(docType);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("docType", docType);
      const r = await fetch(`${BASE}/api/onboarding-portal/${token}/upload`, { method: "POST", body: fd });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || "Upload failed");
      flash(`${j.docType} uploaded`);
      load();
    } catch (e) { flash(e.message, "err"); } finally { setUploading(null); }
  };

  const removeDoc = async (docId) => {
    try {
      const r = await fetch(`${BASE}/api/onboarding-portal/${token}/documents/${docId}`, { method: "DELETE" });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || "Failed to remove");
      flash("Removed");
      load();
    } catch (e) { flash(e.message, "err"); }
  };

  if (loading && !data) return <div className="min-h-screen flex items-center justify-center bg-bg text-t3 text-[15px]">Loading…</div>;
  if (err) return (
    <div className="min-h-screen flex items-center justify-center bg-bg p-4">
      <div className="bg-bg1 border border-rd rounded-2xl p-6 max-w-md text-center">
        <AlertCircle size={32} color={K.r} className="mx-auto mb-2" />
        <div className="text-[15px] font-bold text-t0 mb-1">Link unavailable</div>
        <div className="text-[14px] text-t3">{err}</div>
        <div className="text-[14px] text-t3 mt-3">If you believe this is an error, please contact HR.</div>
      </div>
    </div>
  );

  const submitted = !!data.joiningSubmitted;
  const allUploaded = data.documents.every(d => d.files.length > 0);
  const allFields = FIELD_SECTIONS.flatMap(s => s.fields);
  // Required PF fields still empty; the server re-checks on submit.
  const pfMissing = (data.pfForms || []).flatMap(f =>
    f.sections.flatMap(sec => sec.fields
      .filter(fl => fl.required && (fl.type === "checkbox"
        ? !pf[f.key]?.[fl.id]
        : !String(pf[f.key]?.[fl.id] || "").trim()))
      .map(fl => fl.label)));
  const missingFields = allFields.filter(([key]) => !String(form[key] || "").trim());
  const missingDocs = data.documents.filter(d => d.files.length === 0);
  const canSubmit = missingFields.length === 0 && missingDocs.length === 0 && pfMissing.length === 0;

  // One press sends everything: saves the details and PF answers, then runs
  // the final submit, which is the only step that notifies HR.
  const submitAll = async () => {
    setSaving(true); setMsg(null);
    try {
      const put = (path, body) => fetch(`${BASE}/api/onboarding-portal/${token}/${path}`, {
        method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
      });
      const saves = await Promise.all([put("joining-details", form), put("pf-forms", pf)]);
      for (const r of saves) {
        if (!r.ok) throw new Error((await r.json()).error || "Couldn't save your answers");
      }
      const r = await fetch(`${BASE}/api/onboarding-portal/${token}/submit`, { method: "POST" });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || "Couldn't submit");
      flash("Thank you — your details, PF declarations and documents have been submitted.");
      load();
    } catch (e) { flash(e.message, "err"); } finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen bg-bg p-4 md:p-8">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="bg-bg1 border border-b1 rounded-2xl p-5 mb-4">
          <div className="text-[14px] text-t3 uppercase tracking-wider">Joining Details</div>
          <div className="text-[20px] font-extrabold text-t0 mt-1">Welcome, {data.name}!</div>
          <div className="text-[14px] text-t2 mt-1">
            {data.position}{data.office ? ` · ${data.office}` : ""}{data.joinDate ? ` · Joined ${fmtDDMMYY(data.joinDate)}` : ""}
          </div>
        </div>

        {msg && <div className={`px-3 py-2 mb-3 rounded-lg text-[14px] font-semibold ${msg.kind === "err" ? "bg-rb text-r border border-rd" : "bg-gb text-g border border-gd"}`}>{msg.text}</div>}

        {submitted && (
          <div className="bg-bg1 border border-gd rounded-2xl p-5 mb-4 text-center">
            <CheckCircle2 size={20} color={K.g} className="mx-auto mb-2" />
            <div className="text-[15px] font-bold text-t0">Submitted</div>
            <div className="text-[14px] text-t3 mt-1">Thank you — your details, PF declarations and documents have been submitted. If anything needs correcting, please contact HR.</div>
          </div>
        )}

        {/* Personal / identity / address / employment / family details */}
        {!submitted && (
          <div className="bg-bg1 border border-b1 rounded-2xl p-5 mb-4">
            <div className="text-[14px] text-t3 uppercase tracking-wider mb-3">Joining Details</div>
            <div className="text-[14px] text-t3 mb-4">A few details for your HR record, plus the PF declarations and documents below.</div>
            {FIELD_SECTIONS.map(section => (
              <div key={section.title} className="mb-4">
                <div className="text-[12px] font-semibold text-t2 uppercase tracking-wider mb-2">{section.title}</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {section.fields.map(([key, label, type]) => (
                    <div key={key}>
                      <div className="text-[12px] text-t3 mb-1">{label}</div>
                      <input
                        type={type}
                        value={form[key] || ""}
                        onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                        className="w-full px-3 py-2 bg-bg2 border border-b0 rounded-[10px] text-[14px] text-t0 outline-none"
                      />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Rendered from the form catalogue. */}
        {!submitted && (data.pfForms || []).length > 0 && (
          <div className="bg-bg1 border border-b1 rounded-2xl p-5 mb-4">
            <div className="text-[14px] text-t3 uppercase tracking-wider mb-2">Provident Fund Declarations</div>
            <div className="text-[14px] text-t3 mb-4">Required before your first payroll run. HR completes the employer section of Form 11 afterwards.</div>

            {data.pfForms.map(f => (
              <div key={f.key} className="mb-5">
                <FormRenderer
                  form={f}
                  values={pf[f.key] || {}}
                  onChange={(id, v) => setPf(prev => ({ ...prev, [f.key]: { ...(prev[f.key] || {}), [id]: v } }))}
                />
              </div>
            ))}
          </div>
        )}

        {/* Documents */}
        {!submitted && (
          <div className="bg-bg1 border border-b1 rounded-2xl p-5 mb-4">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[14px] text-t3 uppercase tracking-wider">Documents</div>
              {allUploaded && <div className="text-[14px] font-semibold text-g flex items-center gap-1"><CheckCircle2 size={12} />All uploaded</div>}
            </div>
            <div className="text-[14px] text-t3 mb-3">Please upload the documents below. PDF, JPG, or PNG. Max 10 MB each. Each document type accepts multiple files (e.g. one per month for salary slips) — just click Add again.</div>
            <div className="space-y-3">
              {data.documents.map(d => (
                <div key={d.docType} className="p-3 bg-bg2 rounded-lg border border-b0">
                  <div className="flex items-center gap-3">
                    <FileText size={18} color={d.files.length ? K.g : K.a} />
                    <div className="flex-1 min-w-0">
                      <div className="text-[14px] font-semibold text-t0">{d.docType}</div>
                      <div className="text-[14px] text-t3">{d.files.length ? `${d.files.length} file${d.files.length > 1 ? "s" : ""} uploaded` : "Required"}</div>
                    </div>
                    <label className="cursor-pointer flex items-center gap-1 px-3 py-1 rounded-lg text-[14px] font-semibold shrink-0" style={{ background: K.p + "14", color: K.p, border: `1px solid ${K.p}22` }}>
                      <Upload size={12} />
                      {uploading === d.docType ? "Uploading…" : (d.files.length ? "Add Another" : "Upload")}
                      <input type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" onChange={e => upload(d.docType, e.target.files[0])} disabled={uploading === d.docType} />
                    </label>
                  </div>
                  {d.files.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {d.files.map(f => (
                        <div key={f.id} className="flex items-center gap-2 pl-[30px] text-[14px] text-t2">
                          <span className="flex-1 min-w-0 truncate">{f.fileName || "Uploaded file"}</span>
                          <button onClick={() => removeDoc(f.id)} className="text-t3 hover:text-r border-0 bg-transparent cursor-pointer p-[2px]" title="Remove">
                            <X size={12} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* The single submit — the only press that sends anything to HR. */}
        {!submitted && (
          <div className="bg-bg1 border border-b1 rounded-2xl p-5 mb-4">
            {!canSubmit && (
              <div className="text-[14px] text-a mb-2">
                Complete every part above, then press Submit once.
                {missingFields.length > 0 && <> Missing fields: {missingFields.map(([, label]) => label).join(", ")}.</>}
                {pfMissing.length > 0 && <> PF still needed: {pfMissing.slice(0, 4).join(", ")}{pfMissing.length > 4 ? ` and ${pfMissing.length - 4} more` : ""}.</>}
                {missingDocs.length > 0 && <> Missing documents: {missingDocs.map(d => d.docType).join(", ")}.</>}
              </div>
            )}
            <button
              onClick={submitAll}
              disabled={saving || !canSubmit}
              className="px-4 py-2 rounded-lg text-[14px] font-semibold text-white border-0"
              style={{ background: K.p, cursor: (saving || !canSubmit) ? "not-allowed" : "pointer", opacity: (!canSubmit && !saving) ? 0.5 : 1 }}
            >
              {saving ? "Submitting…" : "Submit"}
            </button>
          </div>
        )}

        <div className="text-[14px] text-t3 text-center mt-4">Questions? Reply to the welcome email from HR.</div>
      </div>
    </div>
  );
};
